import { quantitative, section, number, equation, table, example } from '../batch2/shared.ts';
export const batch4Math = [
  quantitative(
    'math-months',
    'math',
    'Four registrations',
    [
      section('complement', 'Count a collision by first counting its absence', [
        'A probability model must identify equally likely outcomes. Here each person independently selects one of twelve labels with equal probability. An outcome is an ordered list of four labels, one per person. The people remain distinct even when two choose the same label. There are 12⁴ equally likely lists because each position has twelve possible entries.',
        '“At least one match” includes several overlapping possibilities: one pair, two pairs, a triple or four equal labels. Adding probabilities for each possible pair of people would count some outcomes more than once. The complement is simpler: every person’s label is different. An event and its complement partition the entire outcome space, so their probabilities sum to one.',
        equation(
          'Use the complement',
          'P(at least one match)',
          '1 − P(all different)',
          'Every list either has a repeated label or does not; these cases cannot overlap.',
        ),
        example(
          'Three people and five labels',
          'The first person can choose anything. The second avoids that label with probability 4/5. If the first two differ, the third avoids both with probability 3/5. The chance of all different is 1 × 4/5 × 3/5 = 12/25, and the match probability is 13/25.',
          table(
            'Conditional steps',
            ['Person', 'Unused labels', 'Chance of a new label'],
            [
              ['First', '5', '5/5'],
              ['Second, after one distinct label', '4', '4/5'],
              ['Third, after two distinct labels', '3', '3/5'],
            ],
          ),
          'The choices are independent, but the events “is different from everyone earlier” use the earlier outcomes. Multiplying the stated conditional probabilities handles that dependence correctly.',
        ),
        'The number of labels excluded grows after each successful nonmatch. Reusing 11/12 at every step would only avoid one fixed label, not every earlier label. If labels have unequal probabilities, counting available labels alone is insufficient; their probability weights also matter.',
      ]),
      section(
        'apply',
        'Apply the successive restrictions',
        [
          'For four people and twelve labels, the no-match probability is 1 × 11/12 × 10/12 × 9/12. Its numerator counts 12 × 11 × 10 × 9 distinct-label lists out of 12⁴ possible lists.',
          equation(
            'No match',
            'P(all different)',
            '55/96',
            'Cancel common factors in 12 × 11 × 10 × 9 / 12⁴.',
          ),
          equation(
            'Requested probability',
            'P(at least one match)',
            '1 − 55/96 = 41/96',
            'About 42.71% of modeled groups contain a repeated label.',
          ),
          'The pair-count shortcut gives six pairs times 1/12, or one half. That is the expected number of matching pairs, not the probability that at least one exists. A group with three equal labels contributes three matching pairs to that average. The complement method counts each complete group exactly once.',
          'The prompt uses an idealized registration model. It is not an empirical claim that actual birthdays or registrations are equally distributed across months. That assumption is supplied to make the experiment precise; a real seasonal distribution would require different weights.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-months',
      'In a model, each of four distinct people independently receives a registration month, uniformly from the 12 months. What is the probability that at least two people receive the same month? Give a fraction or decimal.',
      41 / 96,
      'probability',
      'All-different lists have probability 55/96; their complement is 41/96.',
      ['complement', 'apply'],
    ),
  ),
  quantitative(
    'math-transferred-ball',
    'math',
    'A transferred ball',
    [
      section('branches', 'Distinguish the hidden step from the observed result', [
        'In a two-stage experiment, an early random event can change the probabilities of a later observation. To reason backward from that observation, first reason forward through every possible early event. A branch weight is the probability of the early event multiplied by the probability of the observation conditional on that event. The product is the probability that both happen.',
        'Once the observation is known, discard branches incompatible with it and rescale the surviving weights to total one. This is conditional probability: restrict the possible outcomes to those consistent with the evidence. A later red draw does not prove that an earlier transferred ball was red if the receiving bag already contained red balls.',
        equation(
          'Rescale compatible branches',
          'P(H given O)',
          'P(H and O) / P(O)',
          'H is a hidden event and O is the observed event; require P(O) > 0.',
        ),
        example(
          'A different hidden choice',
          'Choose box X with probability 1/4 and box Y with probability 3/4. A light flashes with probability 1 in X and 1/3 in Y. The joint weights for choosing each box and seeing a flash are 1/4 and (3/4)(1/3)=1/4. Given a flash, the boxes are therefore equally likely.',
          table(
            'Keep joint weights separate',
            ['Hidden choice', 'Prior chance', 'Flash chance', 'Joint weight'],
            [
              ['X', '1/4', '1', '1/4'],
              ['Y', '3/4', '1/3', '1/4'],
            ],
          ),
          'X made the observation more likely, but it was less likely to be selected initially. Both the prior chance and the observation rate matter.',
        ),
        'The denominator sums the compatible joint weights, not merely the probabilities of the hidden alternatives before the observation. When the hidden cases are mutually exclusive and cover every possibility, their joint weights can be added without double-counting. The daily transfer has exactly two such cases: red transferred or blue transferred.',
      ]),
      section(
        'apply',
        'Update after the red draw',
        [
          'A red transfer has probability 3/5 and leaves the second bag with two red and three blue balls. A blue transfer has probability 2/5 and leaves it with one red and four blue balls. The receiving bag contains five balls in either case.',
          table(
            'Branches consistent with observing red',
            ['Transfer', 'Transfer chance', 'Red draw chance', 'Joint weight'],
            [
              ['Red', '3/5', '2/5', '6/25'],
              ['Blue', '2/5', '1/5', '2/25'],
            ],
          ),
          equation(
            'Conditional transfer probability',
            'P(red transfer given red draw)',
            '(6/25) / (6/25 + 2/25) = 3/4',
            'Only the red-draw branches remain after conditioning.',
          ),
          'Before the draw, a red transfer had chance 3/5. The red observation raises that to 3/4 because it is twice as likely after a red transfer. It does not raise it to certainty: the original red ball in the second bag can also produce the observation after a blue transfer. The requested event concerns the transferred color, not whether the exact physical transferred ball was subsequently drawn.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-transfer',
      'Bag A contains 3 red and 2 blue balls. Bag B contains 1 red and 3 blue balls. Transfer one uniformly chosen ball from A into B, then draw one ball uniformly from B. Given that the draw from B is red, what is the probability that the transferred ball was red? Give a fraction or decimal.',
      3 / 4,
      'probability',
      'The compatible red- and blue-transfer branches weigh 6/25 and 2/25, so the conditional chance is 3/4.',
      ['branches', 'apply'],
    ),
  ),
  quantitative(
    'math-two-payments',
    'math',
    'Two payments',
    [
      section('balance', 'Time the growth and the payment separately', [
        'A balance with compound interest changes by multiplication before a payment changes it by subtraction. If the balance is B and the period’s rate is r, interest adds rB, so the new balance before payment is B + rB = B(1+r). A payment P made at the end of that period then leaves B(1+r) − P. The order matters: a payment at the beginning would reduce the amount on which interest accrues.',
        equation(
          'One end-of-period update',
          'next balance',
          'current balance × (1 + r) − P',
          'Rates are decimals: ten percent corresponds to r = 0.10.',
        ),
        'Applying the same update twice does not mean that every part of the original balance remains outstanding for two full periods. The first payment reduces what remains before the second interest calculation. A ledger makes this timing visible and prevents adding a flat two-period interest amount to the original principal.',
        example(
          'A smaller model',
          'A balance starts at 100 units, earns 20% interest each period and receives equal end-of-period payments P. After the first period it is 120 − P. After the second it is 1.2(120 − P) − P = 144 − 2.2P. Setting the ending balance to zero gives P = 144/2.2, about 65.4545.',
          table(
            'Track the operations',
            ['Stage', 'Balance'],
            [
              ['Initial', '100'],
              ['After first interest', '120'],
              ['After first payment', '120 − P'],
              ['After second interest', '144 − 1.2P'],
              ['After second payment', '144 − 2.2P'],
            ],
          ),
          'The first payment is multiplied by 1.2 in the final expression because removing that money also avoids one period of interest on it. The second payment has no later interest period to affect.',
        ),
        'These are stipulated mathematical cash flows. Actual agreements can specify different compounding intervals, fees and rounding rules. The useful lesson is to translate the stated timing into updates before solving, rather than importing unstated conventions.',
      ]),
      section(
        'apply',
        'Set the final balance to zero',
        [
          'The daily first-period balance after payment is 1100 − P. The second update gives 1.1(1100 − P) − P = 1210 − 2.1P. Setting this equal to zero requires 2.1P = 1210.',
          equation(
            'Equal payment',
            'P',
            '1210 / 2.1 = 12100/21 ≈ 576.190476',
            'The question allows an answer rounded to the nearest cent.',
          ),
          'Using the exact value, the balance after the first payment is 11000/21 units, about 523.809524. Increasing it by ten percent produces 12100/21, exactly the second payment. This independent ledger check returns the final balance to zero.',
          'Paying half of 1000×1.1² would be too little: that divides a two-period future value into two amounts but ignores that the first payment occurs a period earlier and changes subsequent interest. Conversely adding twenty percent simple interest and halving answers a different model. Rounding is allowed only for the final reported payment; the stated calculation itself does not round between periods.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-payments',
      'A hypothetical balance starts at 1,000 units. At the end of each of two periods, 10% interest is first added to the outstanding balance and then an equal payment P is subtracted. There are no fees or intermediate rounding. What payment P makes the balance exactly zero after the second payment? Give the exact value or round to the nearest 0.01 unit.',
      12100 / 21,
      'units per payment',
      'The final balance is 1210 − 2.1P, which is zero when P = 12100/21.',
      ['balance', 'apply'],
      0.0051,
    ),
  ),
  quantitative(
    'math-collecting-types',
    'math',
    'Four packets',
    [
      section('indicators', 'Count each kind once, however often it appears', [
        'The number of different kinds observed is not the number of packets opened. Repeats increase the packet count without adding another kind. A useful way to count distinct kinds is to make one yes-or-no variable for each kind: one if it appears at least once, zero otherwise. These variables are called indicators. Their sum is exactly the number of distinct kinds in that outcome.',
        'An expected value is an average weighted by outcome probabilities. For an indicator, that average is 1×P(appears) + 0×P(absent), which equals P(appears). The average of a sum equals the sum of the averages because each outcome’s weighted sum can be regrouped by kind. This linearity does not require the indicators to be independent.',
        equation(
          'Count distinct kinds by indicators',
          'E(number of kinds)',
          'sum over kinds of P(that kind appears)',
          'Each kind contributes either one or zero, even if it appears repeatedly.',
        ),
        example(
          'Two packets and two kinds',
          'With equally likely independent A/B packets, the four equally likely outcomes are AA, AB, BA and BB. Their distinct-kind counts are 1, 2, 2 and 1, giving mean 6/4 = 1.5. Alternatively, A appears with probability 3/4 and B appears with probability 3/4; adding gives the same 1.5.',
          table(
            'A direct small check',
            ['Outcome', 'Distinct kinds'],
            [
              ['AA', '1'],
              ['AB', '2'],
              ['BA', '2'],
              ['BB', '1'],
            ],
          ),
          'The noninteger expectation is an average across repeated experiments, not a possible count in one experiment.',
        ),
        'For one particular kind, absence is often easier to count than appearance. If its chance on a packet is p and packets are independent, absence across n packets has probability (1−p)ⁿ. Subtracting that from one gives its appearance probability. Independence is used across packets for this product, not between different kinds’ indicators.',
      ]),
      section(
        'apply',
        'Apply the appearance probability to each kind',
        [
          'Each of the three daily kinds has probability 1/3 on each packet, hence absence probability (2/3)⁴ = 16/81 across four packets. Its chance of appearing at least once is 65/81. The three indicators have the same expectation.',
          equation(
            'Expected number of kinds',
            'expected number of distinct types',
            '3 × 65/81 = 65/27',
            'Approximately 2.4074 distinct kinds.',
          ),
          'A separate check classifies all 3⁴ = 81 ordered packet outcomes. Three contain just one kind. For exactly two kinds, choose their pair in three ways and then use both labels in the four slots: 2⁴ − 2 = 14 lists per pair, giving 42 outcomes. The remaining 36 contain all three. The weighted mean is (3×1 + 42×2 + 36×3)/81 = 65/27.',
          'The answer is between one and three, as it must be. Multiplying four packets by a per-packet novelty chance of one would incorrectly treat repeats as new kinds; the probability of novelty changes with what has already appeared. Indicators avoid that changing-state bookkeeping by asking a fixed question of each kind.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-types',
      'Each packet independently contains one of three equally likely sticker types. Open four packets. What is the expected number of distinct types obtained? Give a fraction or decimal.',
      65 / 27,
      'distinct types',
      'Each type appears with probability 1 − (2/3)^4, and the three appearance indicators sum to the distinct count.',
      ['indicators', 'apply'],
    ),
  ),
  quantitative(
    'math-pooled-spread',
    'math',
    'Two groups of measurements',
    [
      section('variance', 'Measure spread around the right center', [
        'A population variance measures the average squared distance of observations from their mean. Subtracting the mean centers the data; squaring prevents positive and negative deviations from canceling and gives larger deviations greater weight. Dividing by the population size averages those squared distances. This problem explicitly uses population variance, not the sample estimator whose denominator is one less than the sample size.',
        equation(
          'Population variance',
          'variance',
          'sum of (observation − mean)² / number of observations',
          'Its units are squared measurement units.',
        ),
        'When groups have different means, two sources of spread exist: variation within each group and separation between the group centers. Averaging the group variances counts only the first. The overall mean must first be calculated by weighting each group mean by its number of observations.',
        example(
          'Two simple groups',
          'Group X contains 1 and 3; group Y contains 5 and 7. Their means are 2 and 6 and both population variances are 1. Together the mean is 4. The four squared deviations from 4 are 9, 1, 1 and 9, so the pooled variance is 5, not 1.',
          table(
            'Two contributions',
            ['Contribution', 'Value'],
            [
              ['Average within-group variance', '1'],
              ['Average squared distance of group means from 4', '4'],
              ['Total population variance', '5'],
            ],
          ),
          'The groups are individually tight but far apart. A pooled description must retain both facts.',
        ),
        'The decomposition can be derived rather than memorized. Write each observation’s displacement from the overall mean as its displacement from its own group mean plus the group mean’s displacement from the overall mean. Squaring gives two square terms and a cross term. Within a group, the individual deviations from that group’s mean sum to zero, so the cross terms cancel when summed.',
      ]),
      section(
        'apply',
        'Combine within-group and between-group spread',
        [
          'The overall mean is (4×10 + 6×20)/10 = 16. The within-group sum of squared deviations is 4×4 + 6×9 = 70. The contribution from separated group means is 4×(10−16)² + 6×(20−16)² = 240.',
          table(
            'Pooled sums of squares',
            ['Group', 'Within group', 'Shift of group center'],
            [
              ['4 observations, mean 10', '4×4 = 16', '4×36 = 144'],
              ['6 observations, mean 20', '6×9 = 54', '6×16 = 96'],
              ['Total', '70', '240'],
            ],
          ),
          equation(
            'Pooled population variance',
            'variance',
            '(70 + 240) / 10 = 31',
            'Both sums are divided by the total population size, ten.',
          ),
          'The standard deviation would be √31, but the prompt asks for variance. A weighted average of 4 and 9 would give 7 and miss the entire between-group contribution. A second check uses mean squares: the groups have average squared observations 4+10²=104 and 9+20²=409. Their weighted mean square is 287, and 287−16²=31.',
          'No individual observations are needed because group size, mean and population variance determine each group’s sum and sum of squares. If the supplied variances instead used sample denominators, reconstructing within-group sums would require multiplying by n−1 rather than n.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-variance',
      'Group A has 4 measurements, mean 10 and population variance 4. Group B has 6 measurements, mean 20 and population variance 9. Pool all 10 measurements. What is their population variance? Here population variance means the average squared deviation, with denominator equal to the group size.',
      31,
      'squared units',
      'The pooled mean is 16; within and between sums of squares are 70 and 240, giving variance 31.',
      ['variance', 'apply'],
      0,
    ),
  ),
];
